(() => {
  if (window.galixWorldRoom) return;

  const worldTints = ['109 85 217', '128 141 205', '64 141 171', '183 118 86', '48 130 156', '92 84 173', '169 129 101', '76 138 205', '181 148 180', '72 123 185', '182 115 73', '114 143 201'];

  function init(section) {
    if (section.worldRoomController) return;
    section.worldsExpandController?.abort();

    const controller = new AbortController();
    const { signal } = controller;
    section.worldRoomController = controller;
    const cards = [...section.querySelectorAll('[data-world]')];
    const rail = section.querySelector('[data-world-rail]');
    const previous = section.querySelector('[data-world-prev]');
    const next = section.querySelector('[data-world-next]');
    const title = section.querySelector('[data-world-title]');
    const description = section.querySelector('[data-world-description]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = matchMedia('(max-width: 699px)');
    let active = Math.max(0, cards.findIndex((card) => card.classList.contains('is-active')));
    let summaryAnimation;
    let resizeObserver;
    let railAnimation = 0;
    let swipeSettleTimer = 0;
    let swipeStartX = null;
    let swipeStartY = null;
    let swipeStartScroll = 0;
    let horizontalSwipe = false;
    let suppressClickUntil = 0;
    let userScrollArmed = false;

    function syncControls() {
      if (!rail) return;
      if (previous) previous.hidden = active === 0;
      if (next) next.hidden = active === cards.length - 1;
    }

    function keepSelectedVisible(card, behavior) {
      if (!rail || !card) return;
      const railRect = rail.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const showNextCard = cards.indexOf(card) < cards.length - 1;
      const rightInset = showNextCard ? Math.min(156, Math.max(80, rail.clientWidth * .11)) : Math.min(22, rail.clientWidth * .06);
      let delta = 0;
      if (cardRect.right > railRect.right - rightInset) delta = cardRect.right - railRect.right + rightInset;
      if (cardRect.left < railRect.left) delta = cardRect.left - railRect.left;
      if (Math.abs(delta) > 1) rail.scrollTo({ left: rail.scrollLeft + delta, behavior });
    }

    function cancelRailAnimation() {
      if (railAnimation) cancelAnimationFrame(railAnimation);
      railAnimation = 0;
    }

    function centerSelected(index, animate) {
      if (!rail || !cards[index]) return;
      if (!mobile.matches) {
        if (animate) requestAnimationFrame(() => keepSelectedVisible(cards[index], reduced.matches ? 'auto' : 'smooth'));
        else keepSelectedVisible(cards[index], 'auto');
        return;
      }

      cancelRailAnimation();
      const styles = getComputedStyle(rail);
      const compact = parseFloat(styles.getPropertyValue('--galix-world-compact')) || 112;
      const gap = parseFloat(styles.columnGap) || 8;
      const expanded = Math.min(window.innerWidth * .76, 320);
      const target = Math.max(0, Math.min(rail.scrollWidth - rail.clientWidth,
        index * (compact + gap) + (expanded - rail.clientWidth) / 2));
      const start = rail.scrollLeft;
      const distance = target - start;
      if (Math.abs(distance) < 1) return;
      if (!animate || reduced.matches || document.hidden) {
        rail.scrollLeft = target;
        syncControls();
        return;
      }

      const started = performance.now();
      const duration = 420;
      function frame(now) {
        const progress = Math.min(1, (now - started) / duration);
        const eased = progress * progress * (3 - 2 * progress);
        rail.scrollLeft = start + distance * eased;
        if (progress < 1) railAnimation = requestAnimationFrame(frame);
        else {
          railAnimation = 0;
          rail.scrollLeft = target;
          syncControls();
        }
      }
      railAnimation = requestAnimationFrame(frame);
    }

    function settleSwipe() {
      if (!rail || !userScrollArmed || !mobile.matches) return;
      userScrollArmed = false;
      const nearest = indexFromScroll();
      if (nearest === active) centerSelected(active, true);
      else select(nearest, true, false, true);
    }

    // Inverso de centerSelected: qué mundo queda centrado para el scrollLeft actual (no depende de qué tarjeta esté abierta).
    function indexFromScroll() {
      const styles = getComputedStyle(rail);
      const compact = parseFloat(styles.getPropertyValue('--galix-world-compact')) || 112;
      const gap = parseFloat(styles.columnGap) || 8;
      const expanded = Math.min(window.innerWidth * .76, 320);
      const index = Math.round((rail.scrollLeft + (rail.clientWidth - expanded) / 2) / (compact + gap));
      return Math.max(0, Math.min(cards.length - 1, index));
    }

    // Móvil: el mundo se abre a medida que el dedo desliza el carril, sin esperar a soltar.
    function followSwipe() {
      if (!rail || !userScrollArmed || !mobile.matches) return;
      const index = indexFromScroll();
      if (index === active) return;
      select(index, true, false, true);
      userScrollArmed = true;
    }

    function scheduleSwipeSettle() {
      clearTimeout(swipeSettleTimer);
      swipeSettleTimer = setTimeout(settleSwipe, 150);
    }

    function animateSummary() {
      const summary = section.querySelector('.galix-worlds__summary > div');
      summaryAnimation?.cancel();
      if (!summary || reduced.matches) return;
      summaryAnimation = summary.animate(
        [{ opacity: .25, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
        { duration: mobile.matches ? 180 : 220, easing: 'cubic-bezier(.22,1,.36,1)' }
      );
    }

    function select(index, animate = true, focus = false, preserveRailPosition = false) {
      const card = cards[index];
      if (!card) return;
      const changed = active !== index;
      active = index;

      cards.forEach((item, itemIndex) => {
        const selected = itemIndex === index;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-pressed', String(selected));
        item.tabIndex = selected ? 0 : -1;
        if (!selected) {
          item.style.setProperty('--world-x', '0px');
          item.style.setProperty('--world-y', '0px');
        }
      });

      if (title) title.textContent = card.dataset.title;
      if (description) description.textContent = card.dataset.description;
      section.dataset.activeWorld = String(index + 1);
      section.style.setProperty('--world-accent', worldTints[index] || worldTints[0]);

      userScrollArmed = false;
      clearTimeout(swipeSettleTimer);
      if (animate && changed) animateSummary();
      syncControls();
      if (!preserveRailPosition) centerSelected(index, animate);
      if (focus) card.focus({ preventScroll: true });
    }

    rail?.addEventListener('click', (event) => {
      if (performance.now() >= suppressClickUntil || !event.target.closest('[data-world]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { signal, capture: true });

    cards.forEach((card, index) => {
      card.addEventListener('click', () => select(index), { signal });
      card.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % cards.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + cards.length) % cards.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = cards.length - 1;
        else return;
        event.preventDefault();
        select(next, true, true);
      }, { signal });

      card.addEventListener('pointermove', (event) => {
        if (reduced.matches || !card.classList.contains('is-active') || event.pointerType === 'touch') return;
        const rect = card.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - .5) * -12;
        const y = ((event.clientY - rect.top) / rect.height - .5) * -9;
        card.style.setProperty('--world-x', `${x.toFixed(2)}px`);
        card.style.setProperty('--world-y', `${y.toFixed(2)}px`);
      }, { signal, passive: true });

      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--world-x', '0px');
        card.style.setProperty('--world-y', '0px');
      }, { signal, passive: true });
      card.addEventListener('transitionend', (event) => {
        if (!mobile.matches && event.propertyName === 'flex-basis' && index === active) keepSelectedVisible(card, reduced.matches ? 'auto' : 'smooth');
      }, { signal });
    });

    rail?.addEventListener('scroll', () => {
      syncControls();
      if (!userScrollArmed) return;
      followSwipe();
      scheduleSwipeSettle();
    }, { signal, passive: true });
    rail?.addEventListener('pointerdown', (event) => {
      if (!mobile.matches || event.pointerType === 'mouse') return;
      cancelRailAnimation();
      swipeStartX = event.clientX;
      swipeStartY = event.clientY;
      swipeStartScroll = rail.scrollLeft;
      horizontalSwipe = false;
      userScrollArmed = false;
    }, { signal, passive: true });
    rail?.addEventListener('pointermove', (event) => {
      if (swipeStartX === null || !mobile.matches || event.pointerType === 'mouse') return;
      const deltaX = event.clientX - swipeStartX;
      const deltaY = event.clientY - swipeStartY;
      if (!horizontalSwipe && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) horizontalSwipe = true;
      if (!horizontalSwipe) return;
      userScrollArmed = true;
      rail.scrollLeft = swipeStartScroll - deltaX;
      followSwipe();
    }, { signal, passive: true });
    rail?.addEventListener('pointerup', () => {
      if (swipeStartX !== null && horizontalSwipe && Math.abs(rail.scrollLeft - swipeStartScroll) > 5) {
        userScrollArmed = true;
        suppressClickUntil = performance.now() + 450;
        scheduleSwipeSettle();
      } else userScrollArmed = false;
      swipeStartX = null;
      swipeStartY = null;
      horizontalSwipe = false;
    }, { signal, passive: true });
    rail?.addEventListener('pointercancel', () => {
      if (swipeStartX !== null && horizontalSwipe && Math.abs(rail.scrollLeft - swipeStartScroll) > 5) {
        userScrollArmed = true;
        suppressClickUntil = performance.now() + 450;
        scheduleSwipeSettle();
      } else {
        userScrollArmed = false;
        clearTimeout(swipeSettleTimer);
      }
      swipeStartX = null;
      swipeStartY = null;
      horizontalSwipe = false;
    }, { signal, passive: true });
    rail?.addEventListener('wheel', (event) => {
      if (!mobile.matches || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      cancelRailAnimation();
      userScrollArmed = true;
      scheduleSwipeSettle();
    }, { signal, passive: true });
    previous?.addEventListener('click', () => {
      if (active === 0) rail?.scrollTo({ left: 0, behavior: reduced.matches ? 'auto' : 'smooth' });
      else select(active - 1, true, true);
    }, { signal });
    next?.addEventListener('click', () => {
      if (active === cards.length - 1) rail?.scrollTo({ left: rail.scrollWidth - rail.clientWidth, behavior: reduced.matches ? 'auto' : 'smooth' });
      else select(active + 1, true, true);
    }, { signal });
    if ('ResizeObserver' in window && rail) {
      resizeObserver = new ResizeObserver(() => {
        if (mobile.matches) centerSelected(active, false);
        else keepSelectedVisible(cards[active], 'auto');
        syncControls();
      });
      resizeObserver.observe(rail);
    }

    reduced.addEventListener('change', () => {
      summaryAnimation?.cancel();
      if (reduced.matches) centerSelected(active, false);
    }, { signal });
    signal.addEventListener('abort', () => {
      summaryAnimation?.cancel();
      cancelRailAnimation();
      clearTimeout(swipeSettleTimer);
      resizeObserver?.disconnect();
      delete section.worldRoomController;
    }, { once: true });

    select(active, false);
  }

  function mount(root = document) {
    if (root.matches?.('[data-world-room]')) init(root);
    root.querySelectorAll('[data-world-room]').forEach(init);
  }

  window.galixWorldRoom = { mount };
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-world-room]')) event.target.worldRoomController?.abort();
    event.target.querySelectorAll('[data-world-room]').forEach((section) => section.worldRoomController?.abort());
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
